/* The bell.
 *
 * A badge, and a panel that only fetches when it is opened — otherwise every
 * page load pays for a list nobody asked to see. The badge itself is polled on
 * a slow interval; see useUnreadCount for why that is not a websocket.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import {
  Bell,
  BellRing,
  Check,
  CheckCheck,
  HeartHandshake,
  CalendarPlus,
  Loader2,
  MessageCircle,
  BarChart3,
  ThumbsUp,
  UserPlus,
  XCircle,
  Megaphone,
  Phone,
  Star,
} from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { canAskForPush, enablePush } from '@/api/push'
import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
  useUnreadCount,
} from '@/api/queries'
import { cn, timeAgo } from '@/lib/utils'
import type { Notification, NotificationType } from '@/types/api'

/* An icon per event, so the list is scannable without reading every line. */
const ICONS: Record<NotificationType, typeof Bell> = {
  admin_request_approved: Check,
  admin_request_declined: XCircle,
  member_joined: UserPlus,
  post_liked: ThumbsUp,
  post_commented: MessageCircle,
  prayer_response: HeartHandshake,
  // Group membership, both directions. These were missing since invitations
  // shipped — the maps are exhaustive Records, so the build refuses to let a
  // new NotificationType through without an icon and a tone. That is the
  // point: the bell cannot render a type nobody styled.
  group_invitation: UserPlus,
  group_join_requested: UserPlus,
  group_join_approved: Check,
  group_join_declined: XCircle,
  // Group activity, and the answers coming back to whoever asked.
  group_event_created: CalendarPlus,
  group_event_answered: CalendarPlus,
  group_poll_created: BarChart3,
  group_poll_answered: BarChart3,
  // The prayer you prayed for was answered — the star Testimonies wear
  // everywhere else.
  testimony_shared: Star,
  // The church wrote to everyone.
  church_message: Megaphone,
  call_scheduled: Phone,
}

/* Colour carries meaning, not decoration: approval is success, a hold is a
   warning rather than an error — nothing has gone wrong, it is just waiting. */
const TONES: Record<NotificationType, string> = {
  admin_request_approved: 'bg-success-soft text-success',
  admin_request_declined: 'bg-warning-soft text-warning-ink',
  member_joined: 'bg-brand-soft text-brand',
  post_liked: 'bg-brand-soft text-brand',
  post_commented: 'bg-brand-soft text-brand',
  prayer_response: 'bg-pr-green-soft text-pr-green-ink',
  group_invitation: 'bg-brand-soft text-brand',
  // A request is waiting on somebody, not a success or a failure.
  group_join_requested: 'bg-warning-soft text-warning-ink',
  group_join_approved: 'bg-success-soft text-success',
  group_join_declined: 'bg-warning-soft text-warning-ink',
  // Something to come to, and somebody answering it. Neither is a success or
  // a failure — they are the group getting on with itself.
  group_event_created: 'bg-brand-soft text-brand',
  group_event_answered: 'bg-brand-soft text-brand',
  group_poll_created: 'bg-brand-soft text-brand',
  group_poll_answered: 'bg-brand-soft text-brand',
  // Green, because Answered is green everywhere in Prayer.
  testimony_shared: 'bg-pr-green-soft text-pr-green-ink',
  church_message: 'bg-brand-soft text-brand',
  call_scheduled: 'bg-brand-soft text-brand',
}

export function NotificationBell({ routeFor: routeForProp }: BellProps = {}) {
  const [open, setOpen] = useState(false)
  const { data: count } = useUnreadCount()
  const unread = count?.unread_count ?? 0

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="relative grid h-10 w-10 place-items-center rounded-pill bg-surface-2 text-ink transition-colors hover:bg-hover-2"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'
          }
        >
          <Bell className="h-5 w-5" aria-hidden />
          {unread > 0 ? (
            <span
              className="absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-pill bg-danger px-1 text-xs font-bold text-white"
              aria-hidden
            >
              {/* Anything past 99 is "lots" — the exact number stops being
                  information and starts being a layout problem. */}
              {unread > 99 ? '99+' : unread}
            </span>
          ) : null}
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-[min(380px,calc(100vw-24px))] rounded-md bg-surface shadow-3"
        >
          <NotificationPanel
            open={open}
            onNavigate={() => setOpen(false)}
            routeFor={routeForProp}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

export interface BellProps {
  /** Where a notification leads. The member app and the Administration console
   *  share this bell but not their routes — a church notification means "open
   *  the feed" in one and "open the approval queue" in the other. Defaults to
   *  the member app's map. */
  routeFor?: (n: Notification) => string | null
}

function NotificationPanel({
  open,
  onNavigate,
  routeFor: routeForProp,
}: {
  open: boolean
  onNavigate: () => void
  routeFor?: (n: Notification) => string | null
}) {
  const navigate = useNavigate()
  // `open` gates the fetch: closed bells cost nothing.
  const list = useNotifications(open)
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  const items = list.data?.pages.flatMap((p) => p.items) ?? []
  const hasUnread = items.some((n) => !n.read)

  function tap(n: Notification) {
    if (!n.read) markRead.mutate(n.id)
    const to = (routeForProp ?? routeFor)(n)
    onNavigate()
    if (to) navigate(to)
  }

  return (
    <div className="flex max-h-[70vh] flex-col">
      <header className="flex items-center justify-between border-b border-line-2 px-s4 py-s3">
        <h2 className="text-md font-bold">Notifications</h2>
        {hasUnread ? (
          <button
            onClick={() => markAllRead.mutate()}
            className="flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden />
            Mark all read
          </button>
        ) : null}
      </header>

      {/* The one place asking for permission makes sense: the bell is open, so
          notifications are already what they are thinking about. Shown only
          when the browser can be asked — once they answer, in either
          direction, it disappears rather than nagging. */}
      <PushOptIn />

      <div className="overflow-y-auto">
        {list.isLoading ? (
          <div className="flex justify-center py-s6">
            <Loader2
              className="h-5 w-5 animate-spin text-ink-3"
              aria-label="Loading notifications"
            />
          </div>
        ) : null}

        {!list.isLoading && items.length === 0 ? (
          <div className="px-s5 py-s8 text-center">
            <Bell className="mx-auto h-8 w-8 text-ink-3" aria-hidden />
            <p className="mt-s3 text-md font-semibold">Nothing yet</p>
            <p className="mt-1 text-base text-ink-2">
              You will hear about likes, comments and prayers here.
            </p>
          </div>
        ) : null}

        <ul>
          {items.map((n) => {
            const Icon = ICONS[n.type] ?? Bell
            return (
              <li key={n.id}>
                <button
                  onClick={() => tap(n)}
                  className={cn(
                    'flex w-full gap-s3 px-s4 py-s3 text-left transition-colors hover:bg-hover',
                    // A quiet tint rather than a dot: the whole row reads as
                    // unread at a glance, without hunting for a marker.
                    !n.read && 'bg-brand-softer',
                  )}
                >
                  <div className="relative shrink-0">
                    {n.actor ? (
                      <Avatar
                        name={n.actor.name}
                        src={n.actor.avatar_url}
                        size="md"
                      />
                    ) : (
                      <span className="grid h-10 w-10 place-items-center rounded-pill bg-surface-2">
                        <Icon className="h-5 w-5 text-ink-2" aria-hidden />
                      </span>
                    )}
                    {n.actor ? (
                      <span
                        className={cn(
                          'absolute -bottom-0.5 -right-0.5 grid h-[18px] w-[18px] place-items-center rounded-pill ring-2 ring-surface',
                          TONES[n.type] ?? 'bg-surface-2 text-ink-2',
                        )}
                        aria-hidden
                      >
                        <Icon className="h-2.5 w-2.5" />
                      </span>
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p
                      className={cn(
                        'text-base leading-normal',
                        !n.read && 'font-semibold',
                      )}
                    >
                      {n.title}
                    </p>
                    {n.body ? (
                      <p className="mt-0.5 truncate text-sm text-ink-2">
                        {n.body}
                      </p>
                    ) : null}
                    <p className="mt-0.5 text-sm text-ink-3">
                      {timeAgo(n.created_at)}
                    </p>
                  </div>
                </button>
              </li>
            )
          })}
        </ul>

        {list.hasNextPage ? (
          <button
            onClick={() => list.fetchNextPage()}
            disabled={list.isFetchingNextPage}
            className="w-full border-t border-line-2 py-s3 text-sm font-semibold text-ink-2 hover:bg-hover"
          >
            {list.isFetchingNextPage ? 'Loading…' : 'See earlier'}
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** Where a notification goes when tapped.
 *
 *  The server sends an entity type and id rather than a URL, so routing stays
 *  the client's business — the API does not need to know this app's paths, and
 *  the mobile app can route the same data differently.
 */
function routeFor(n: Notification): string | null {
  /* The comment to open, when the notification names one. Landing on the right
     Prayer Request is not the same as finding the reply: a busy one has several
     collapsed threads and the reader has to open each in turn. */
  const focus = n.sub_entity_id ? `&comment=${n.sub_entity_id}` : ''

  switch (n.entity_type) {
    case 'prayer_request':
      return `/prayer?request=${n.entity_id}${focus}`
    case 'post':
      // Single-post view is not built yet; the feed is where the post lives.
      return `/?post=${n.entity_id}${focus}`
    case 'message':
      // A church announcement. NOWHERE TO GO, deliberately: the bell entry
      // carries the whole message, and the page that lists them is the
      // sender's, which a member may not open. Returning null marks it read
      // and closes the panel rather than navigating somewhere useless.
      return null
    case 'call':
      // The feed, where the card for it rides at the top. Not a page of its
      // own: a member has no Calls screen, and the card carries everything
      // the bell entry could say anyway.
      return '/'
    case 'group':
      // A new event or poll in a group. The TYPE picks the tab, because the
      // entity is the group either way — without this the bell showed "New
      // poll in Youth Fellowship" and tapping it did nothing at all.
      //
      // sub_entity_id is the event or poll itself, passed on so the tab can
      // scroll to it rather than leaving somebody to find it in a long list.
      if (n.type === 'group_event_created' || n.type === 'group_event_answered') {
        return `/groups/${n.entity_id}?tab=events${
          n.sub_entity_id ? `&event=${n.sub_entity_id}` : ''
        }`
      }
      if (n.type === 'group_poll_created' || n.type === 'group_poll_answered') {
        return `/groups/${n.entity_id}?tab=polls${
          n.sub_entity_id ? `&poll=${n.sub_entity_id}` : ''
        }`
      }
      // Somebody is waiting to be approved. The Requests tab is where the
      // Approve and Decline buttons are — the front page only says the group
      // exists, which leaves the reader to find the tab themselves after being
      // told there is something to do.
      if (n.type === 'group_join_requested') {
        return `/groups/${n.entity_id}?tab=requests`
      }
      // Approved, declined, or invited: the group's own page. There is nothing
      // to act on, and Feed is what somebody who just joined wants to see.
      return `/groups/${n.entity_id}`
    case 'church':
      return '/'
    default:
      return null
  }
}

/** "Turn on notifications", offered once inside the open bell.
 *
 *  Hidden unless the browser can actually be asked: already granted, already
 *  denied, or unsupported all mean there is nothing useful to offer, and a
 *  button that does nothing is worse than no button.
 */
function PushOptIn() {
  const [canAsk, setCanAsk] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    canAskForPush().then((ok) => alive && setCanAsk(ok))
    return () => {
      alive = false
    }
  }, [])

  if (!canAsk) return null

  return (
    <div className="flex items-center gap-s3 border-b border-line-2 bg-brand-softer px-s4 py-s3">
      <BellRing className="h-4 w-4 shrink-0 text-brand" aria-hidden />
      <p className="flex-1 text-sm text-ink-2">
        Hear about replies and prayers even when this tab is closed.
      </p>
      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true)
          await enablePush()
          // Either way the offer is done with: granted needs no button, and
          // declined must not be asked again.
          setCanAsk(false)
        }}
        className="shrink-0 text-sm font-semibold text-brand hover:underline disabled:opacity-60"
      >
        Turn on
      </button>
    </div>
  )
}
